import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { ManualLossStreakEditor } from './ManualLossStreakEditor';

interface Bot {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface ConditionalSkipSetting {
  id: string;
  bot_id: string;
  enabled: boolean;
  forward_enabled: boolean;
  skip_type: string;
  loss_streak_threshold: number;
  max_loss_streak?: number | null;
  max_loss_behavior: string;
  forward_url: string | null;
  bots?: Bot;
}

interface BotState {
  id: string;
  bot_id: string;
  current_loss_streak: number;
  mode: 'GATE' | 'OPEN';
  locked_by_max_loss: boolean;
}

interface Type1EditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setting: ConditionalSkipSetting;
  state?: BotState;
  onUpdate: (id: string, updates: any) => void;
  isUpdating: boolean;
}

export const Type1EditDialog: React.FC<Type1EditDialogProps> = ({
  open,
  onOpenChange,
  setting,
  state,
  onUpdate,
  isUpdating,
}) => {
  const [threshold, setThreshold] = useState(setting.loss_streak_threshold);
  const [maxLoss, setMaxLoss] = useState<number | null>(setting.max_loss_streak || null);
  const [behavior, setBehavior] = useState(setting.max_loss_behavior);
  const [forwardUrl, setForwardUrl] = useState(setting.forward_url || '');

  useEffect(() => {
    if (open) {
      setThreshold(setting.loss_streak_threshold);
      setMaxLoss(setting.max_loss_streak || null);
      setBehavior(setting.max_loss_behavior);
      setForwardUrl(setting.forward_url || '');
    }
  }, [open, setting]);

  const handleSave = () => {
    onUpdate(setting.id, {
      loss_streak_threshold: threshold,
      max_loss_streak: maxLoss,
      max_loss_behavior: behavior,
      forward_url: forwardUrl,
    });
    onOpenChange(false);
  };

  const hasChanges = 
    threshold !== setting.loss_streak_threshold ||
    maxLoss !== setting.max_loss_streak ||
    behavior !== setting.max_loss_behavior ||
    forwardUrl !== setting.forward_url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Configuration: {setting.bots?.name}</DialogTitle>
          <DialogDescription>
            Update Type 1 (Start/Stop After Loss) settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current State */}
          <div className="bg-muted p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-sm">Current State</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Mode:</span>
                <Badge variant={state?.mode === 'OPEN' ? 'default' : 'secondary'} className="ml-2">
                  {state?.mode || 'GATE'}
                </Badge>
                {state?.locked_by_max_loss && (
                  <Badge variant="destructive" className="ml-2">LOCKED</Badge>
                )}
              </div>
              <div>
                <span className="text-muted-foreground">Streak:</span>
                <span className="ml-2 font-mono font-semibold">
                  {state?.current_loss_streak || 0}
                </span>
              </div>
              <div>
                {state && (
                  <ManualLossStreakEditor
                    botId={setting.bot_id}
                    currentStreak={state.current_loss_streak || 0}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Configuration */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="threshold">Loss Streak Threshold</Label>
              <Input
                id="threshold"
                type="number"
                min="0"
                value={threshold}
                onChange={(e) => setThreshold(parseInt(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                0 = Always open until max loss streak. 1+ = Open after this many consecutive losses.
              </p>
            </div>

            <div>
              <Label htmlFor="maxLoss">Max Loss Streak (Optional)</Label>
              <Input
                id="maxLoss"
                type="number"
                min="1"
                placeholder="Leave empty to disable"
                value={maxLoss || ''}
                onChange={(e) => setMaxLoss(e.target.value ? parseInt(e.target.value) : null)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                When this streak is reached, behavior depends on Max Loss Behavior setting
              </p>
            </div>

            <div>
              <Label htmlFor="behavior">Max Loss Behavior</Label>
              <Select value={behavior} onValueChange={setBehavior}>
                <SelectTrigger id="behavior">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reset_streak">Reset Streak (Default)</SelectItem>
                  <SelectItem value="lock_gate">Lock Gate</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Reset Streak: Resets loss streak to 0. Lock Gate: Preserves streak but locks gate until winning EXIT.
              </p>
            </div>

            <div>
              <Label htmlFor="forwardUrl">Forward URL</Label>
              <Input
                id="forwardUrl"
                type="url"
                value={forwardUrl}
                onChange={(e) => setForwardUrl(e.target.value)}
                placeholder="https://example.com/webhook"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Webhook URL for signal forwarding
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isUpdating}>
            {isUpdating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
